import type { FC } from 'react';
import { match } from 'ts-pattern';
import { Stack } from '@/components/stack';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Typography } from '@/components/ui/typography';
import { FormSafeButton } from '@/hooks/use-form';
import { db } from '@/integrations/idb/db';

type ErrorAlert = {
  kind: 'error';
  error: Error;
  onClick: () => void;
};

type NewVersionAlert = {
  kind: 'newVersion';
};

export type RouterAlertProps = ErrorAlert | NewVersionAlert;

const RouterAlert: FC<RouterAlertProps> = (props) =>
  match(props)
    .with({ kind: 'error' }, ({ onClick, error }) => (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription className="space-y-2">
          <Typography>
            {import.meta.env.DEV
              ? error.message
              : 'Ops! Something went wrong, please click reload try again.'}
          </Typography>

          <Stack direction="row" spacing={2}>
            <FormSafeButton onClick={onClick} variant="outline">
              Reload
            </FormSafeButton>
            <FormSafeButton
              onClick={async () => {
                try {
                  await db.delete();
                } catch {
                  const databases = await window.indexedDB.databases();

                  databases.forEach((d) => {
                    if (!d.name) {
                      return;
                    }

                    window.indexedDB.deleteDatabase(d.name);
                  });
                } finally {
                  window.localStorage.clear();
                  window.sessionStorage.clear();
                  window.location.assign('/');
                }
              }}
              variant="destructive"
            >
              Force Reload
            </FormSafeButton>
          </Stack>
        </AlertDescription>
      </Alert>
    ))
    .with({ kind: 'newVersion' }, () => (
      <Alert variant="info">
        <AlertTitle>Info</AlertTitle>
        <AlertDescription className="space-y-2">
          <Typography>
            An updated version has been released. Please reload the page.
          </Typography>

          <FormSafeButton
            onClick={() => {
              window.location.reload();
            }}
            variant="outline"
          >
            Reload
          </FormSafeButton>
        </AlertDescription>
      </Alert>
    ))
    .exhaustive();

export default RouterAlert;
